import { createBrowserRouter, Outlet } from "react-router";
import { Suspense } from "react";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SuperAdminRoute } from "./components/SuperAdminRoute";
import { DashboardLayout } from "./components/DashboardLayout";
import { RouteErrorBoundary } from "./components/ErrorBoundary";
import { StealthGuard } from "./components/StealthGuard";
import { builderRouteLoader } from "../lib/builderRouteLoader";
import { lazyRetry } from "./utils/lazyRetry";

// Lazy load route components for code splitting with retry on chunk load errors
const Login = lazyRetry(() => import("./components/Login").then(m => ({ default: m.Login })));
const ForgotPassword = lazyRetry(() => import("./components/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazyRetry(() => import("./components/ResetPassword").then(m => ({ default: m.ResetPassword })));
const VerifyEmail = lazyRetry(() => import("./components/VerifyEmail").then(m => ({ default: m.VerifyEmail })));
const Signup = lazyRetry(() => import("./components/Signup").then(m => ({ default: m.Signup })));
const Dashboard = lazyRetry(() => import("./components/kovadash").then(m => ({ default: m.Dashboard })));
const ItineraryBuilder = lazyRetry(() => import("./components/ItineraryBuilder").then(m => ({ default: m.ItineraryPage })));
const ItineraryPrint = lazyRetry(() => import("./components/ItineraryPrint").then(m => ({ default: m.ItineraryPrint })));
const ItineraryAccess = lazyRetry(() => import("./components/ItineraryAccess").then(m => ({ default: m.ItineraryAccess })));
const Clients = lazyRetry(() => import("./components/Clients").then(m => ({ default: m.Clients })));
const Travel = lazyRetry(() => import("./components/Travel").then(m => ({ default: m.Travel })));
const Guides = lazyRetry(() => import("./components/Guides").then(m => ({ default: m.Guides })));
const Suppliers = lazyRetry(() => import("./components/Suppliers").then(m => ({ default: m.Suppliers })));
const Settings = lazyRetry(() => import("./components/Settings").then(m => ({ default: m.Settings })));
const NotFound = lazyRetry(() => import("./components/NotFound").then(m => ({ default: m.NotFound })));
const PrivacyPolicy = lazyRetry(() => import("./components/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazyRetry(() => import("./components/TermsOfService").then(m => ({ default: m.TermsOfService })));
const SuperAdminPanel = lazyRetry(() => import("./components/SuperAdminPanel").then(m => ({ default: m.SuperAdminPanel })));
const Landing_1 = lazyRetry(() => import("./components/Landing_1").then(m => ({ default: m.Landing_1 })));

// Loading fallback component (full screen)
const LoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    backgroundColor: '#FBF9F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <div style={{ width: '240px', textAlign: 'center' }}>
      <p style={{
        fontSize: '10px',
        color: 'rgba(65,47,33,0.6)',
        textTransform: 'uppercase',
        letterSpacing: '0.2em',
        marginBottom: '12px'
      }}>
        Loading travels...
      </p>
      <div style={{
        height: '2px',
        backgroundColor: 'rgba(65,47,33,0.1)',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: '40%',
          backgroundColor: 'rgba(65,47,33,0.4)',
          animation: 'loading 1.5s ease-in-out infinite',
        }} />
      </div>
    </div>
    <style>{`
      @keyframes loading {
        0%, 100% { transform: translateX(-100%); }
        50% { transform: translateX(250%); }
      }
    `}</style>
  </div>
);

// Content loading fallback (for inside DashboardLayout)
const ContentLoadingFallback = () => (
  <div style={{
    maxWidth: '1380px',
    margin: '0 auto',
    padding: '40px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px'
  }}>
    <div style={{ width: '240px', textAlign: 'center' }}>
      <p style={{
        fontSize: '10px',
        color: 'rgba(65,47,33,0.6)',
        textTransform: 'uppercase',
        letterSpacing: '0.2em',
        marginBottom: '12px'
      }}>
        Loading travels...
      </p>
      <div style={{
        height: '2px',
        backgroundColor: 'rgba(65,47,33,0.1)',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: '40%',
          backgroundColor: 'rgba(65,47,33,0.4)',
          animation: 'loading 1.5s ease-in-out infinite',
        }} />
      </div>
    </div>
    <style>{`
      @keyframes loading {
        0%, 100% { transform: translateX(-100%); }
        50% { transform: translateX(250%); }
      }
    `}</style>
  </div>
);

// Keep the access guard mounted when moving between agency screens.
const ProtectedAgencyLayout = () => (
  <ProtectedRoute><Outlet /></ProtectedRoute>
);

const BuilderRoute = () => (
  <Suspense fallback={<LoadingFallback />}><ItineraryBuilder /></Suspense>
);

const PrintRoute = () => (
  <Suspense fallback={<LoadingFallback />}><ItineraryPrint /></Suspense>
);

export const router = createBrowserRouter([
  {
    // Stealth guard wraps ALL routes - blocks main kovatravel.com domain
    element: <StealthGuard />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/",
        Component: Login,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/login",
        Component: Login,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/forgot-password",
        Component: ForgotPassword,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/reset-password",
        Component: ResetPassword,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/signup",
        Component: Signup,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/verify-email",
        Component: VerifyEmail,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/privacy",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PrivacyPolicy />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/terms-of-service",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <TermsOfService />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/landing",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Landing_1 />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        element: <ProtectedAgencyLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            // Parent route with DashboardLayout
            element: <DashboardLayout />,
            errorElement: <RouteErrorBoundary />,
            children: [
              {
                path: "/kovadash",
                element: (
                  <Suspense fallback={<ContentLoadingFallback />}>
                    <Dashboard />
                  </Suspense>
                ),
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: "/clients",
                element: (
                  <Suspense fallback={<ContentLoadingFallback />}>
                    <Clients />
                  </Suspense>
                ),
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: "/travel",
                element: (
                  <Suspense fallback={<ContentLoadingFallback />}>
                    <Travel />
                  </Suspense>
                ),
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: "/guides",
                element: (
                  <Suspense fallback={<ContentLoadingFallback />}>
                    <Guides />
                  </Suspense>
                ),
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: "/suppliers",
                element: (
                  <Suspense fallback={<ContentLoadingFallback />}>
                    <Suppliers />
                  </Suspense>
                ),
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: "/settings",
                element: (
                  <Suspense fallback={<ContentLoadingFallback />}>
                    <Settings />
                  </Suspense>
                ),
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: "/settings/:section",
                element: (
                  <Suspense fallback={<ContentLoadingFallback />}>
                    <Settings />
                  </Suspense>
                ),
                errorElement: <RouteErrorBoundary />,
              },
            ],
          },
          {
            path: "/newitinerary/:id",
            Component: BuilderRoute,
            loader: builderRouteLoader,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: "/itinerary/:id/print",
            Component: PrintRoute,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
      {
        path: "/itinerary/:id",
        Component: ItineraryAccess,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/sys-panel-f2k6",
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminPanel />
            </Suspense>
          </SuperAdminRoute>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "*",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <NotFound />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
    ],
  },
]);